import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Globe, 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  Upload,
  Search,
  RefreshCw,
  X,
  FileSpreadsheet,
  Download,
  FileUp
} from "lucide-react";

const API = "/api";

// Para birimleri
const CURRENCIES = ["USD", "EUR", "GBP", "TRY"];

// Incoterm kodları
const INCOTERM_CODES = ["DDU", "DDP", "EXW", "FOB", "CIF", "DAP"];

// Zone değerleri
const ZONES = ["ROW", "EU", "NULL"];

// Boş form verisi
const emptyFormData = {
  countryCode: "",
  countryDescription: "",
  ulkeParaBirimi: "USD",
  navlunBedeli: 0,
  navlunParaBirimi: "USD",
  navlunIskontosu: 0,
  incotermCode: "DDU",
  durumaGoreIncoterm: "DDU",
  ithalatVergi: 0,
  zone: "ROW"
};

export default function IncotermPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // File input ref
  const fileInputRef = useRef(null);
  
  // Data
  const [incoterms, setIncoterms] = useState([]);
  const [filteredIncoterms, setFilteredIncoterms] = useState([]);
  
  // Search
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchIncoterms();
  }, [token]);

  useEffect(() => {
    // Filter incoterms based on search
    if (searchTerm) {
      const filtered = incoterms.filter(item => 
        item.countryCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.countryDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.zone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredIncoterms(filtered);
    } else {
      setFilteredIncoterms(incoterms);
    }
  }, [searchTerm, incoterms]);

  const fetchIncoterms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/incoterms?token=${token}`);
      setIncoterms(response.data.incoterms || []);
    } catch (error) {
      console.error("Incoterm listesi alınamadı:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setFormData(emptyFormData);
    setEditMode(false);
    setCurrentId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setFormData({
      countryCode: item.countryCode || "",
      countryDescription: item.countryDescription || "",
      ulkeParaBirimi: item.ulkeParaBirimi || "USD",
      navlunBedeli: item.navlunBedeli || 0,
      navlunParaBirimi: item.navlunParaBirimi || "USD",
      navlunIskontosu: item.navlunIskontosu || 0,
      incotermCode: item.incotermCode || "DDU",
      durumaGoreIncoterm: item.durumaGoreIncoterm || "DDU",
      ithalatVergi: item.ithalatVergi || 0,
      zone: item.zone || "ROW"
    });
    setEditMode(true);
    setCurrentId(item.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.countryCode || !formData.countryDescription) {
      toast.error("Ülke kodu ve adı zorunludur");
      return;
    }

    setSaving(true);
    try {
      if (editMode) {
        // Update
        await axios.put(`${API}/incoterms/${currentId}?token=${token}`, formData);
        toast.success("Incoterm güncellendi");
      } else {
        // Create
        await axios.post(`${API}/incoterms?token=${token}`, formData);
        toast.success("Incoterm eklendi");
      }
      setDialogOpen(false);
      fetchIncoterms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/incoterms/${deleteId}?token=${token}`);
      toast.success("Incoterm silindi");
      setDeleteDialogOpen(false);
      fetchIncoterms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silme başarısız");
    }
  };

  const syncToNebim = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/incoterms/sync-to-nebim?token=${token}`);
      if (response.data.success) {
        toast.success(response.data.message || "Nebim'e senkronize edildi");
      } else {
        toast.error(response.data.error || "Senkronizasyon başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nebim senkronizasyonu başarısız");
    } finally {
      setSyncing(false);
    }
  };

  // Excel Upload
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Dosya tipini kontrol et
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Sadece Excel dosyaları (.xlsx, .xls) kabul edilir");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/incoterms/upload-excel?token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResult(response.data);
      setUploadDialogOpen(true);
      fetchIncoterms();
      
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Excel yükleme başarısız");
    } finally {
      setUploading(false);
      // Input'u temizle
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Şablon İndir
  const downloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/incoterms/download-template?token=${token}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'incoterm_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Şablon indirildi");
    } catch (error) {
      toast.error("Şablon indirme başarısız");
    }
  };

  // Excel Export
  const exportExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/incoterms/export-excel?token=${token}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incoterms_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel indirildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Excel indirme başarısız");
    }
  };

  const getZoneBadgeColor = (zone) => {
    switch (zone) {
      case "EU": return "bg-blue-500";
      case "ROW": return "bg-green-500";
      default: return "bg-gray-400";
    }
  };

  const getIncotermBadgeColor = (code) => {
    switch (code) {
      case "DDP": return "bg-purple-500";
      case "DDU": return "bg-orange-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="incoterm-page">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls"
        className="hidden"
        data-testid="file-input"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Incoterm Yönetimi</h1>
          <p className="text-slate-500 mt-1">Yurt dışı operasyonları için Incoterm tanımları</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {/* Excel İşlemleri */}
          <Button 
            onClick={downloadTemplate}
            variant="outline"
            className="border-gray-300"
            data-testid="btn-download-template"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Şablon İndir
          </Button>
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="border-green-500 text-green-600 hover:bg-green-50"
            data-testid="btn-upload-excel"
          >
            {uploading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="w-4 h-4 mr-2" />
            )}
            Excel Yükle
          </Button>
          <Button 
            onClick={exportExcel}
            disabled={incoterms.length === 0}
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
            data-testid="btn-export-excel"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel İndir
          </Button>
          <Button 
            onClick={syncToNebim}
            disabled={syncing}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="btn-sync-nebim"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Nebim'e Kaydet
          </Button>
          <Button 
            onClick={openAddDialog}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="btn-add-incoterm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Ekle
          </Button>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="md:col-span-2">
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Ülke kodu, adı veya zone ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{incoterms.length}</p>
                <p className="text-xs text-slate-500">Toplam Ülke</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Globe className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {incoterms.filter(i => i.zone === "EU").length}
                </p>
                <p className="text-xs text-slate-500">EU Ülkeleri</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-500" />
            Incoterm Listesi
          </CardTitle>
          <CardDescription>
            {filteredIncoterms.length} kayıt listeleniyor
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredIncoterms.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Henüz Incoterm tanımı yok</p>
              <Button onClick={openAddDialog} className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                İlk Kaydı Ekle
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Kod</TableHead>
                    <TableHead>Ülke</TableHead>
                    <TableHead className="text-center">Para Birimi</TableHead>
                    <TableHead className="text-right">Navlun</TableHead>
                    <TableHead className="text-right">İskonto</TableHead>
                    <TableHead className="text-center">Incoterm</TableHead>
                    <TableHead className="text-center">Duruma Göre</TableHead>
                    <TableHead className="text-right">İthalat Vergi</TableHead>
                    <TableHead className="text-center">Zone</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredIncoterms.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono font-medium">
                        {item.countryCode}
                      </TableCell>
                      <TableCell>{item.countryDescription}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{item.ulkeParaBirimi}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.navlunBedeli?.toFixed(2)} {item.navlunParaBirimi}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.navlunIskontosu?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getIncotermBadgeColor(item.incotermCode)}>
                          {item.incotermCode}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getIncotermBadgeColor(item.durumaGoreIncoterm)}>
                          {item.durumaGoreIncoterm}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.ithalatVergi?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getZoneBadgeColor(item.zone)}>
                          {item.zone || "NULL"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEditDialog(item)}
                            className="h-8 w-8"
                            data-testid={`btn-edit-${item.countryCode}`}
                          >
                            <Pencil className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openDeleteDialog(item.id)}
                            className="h-8 w-8"
                            data-testid={`btn-delete-${item.countryCode}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Incoterm Düzenle" : "Yeni Incoterm Ekle"}
            </DialogTitle>
            <DialogDescription>
              Ülke bazlı navlun ve incoterm bilgilerini girin
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Ülke Kodu */}
            <div className="space-y-2">
              <Label htmlFor="countryCode">Ülke Kodu *</Label>
              <Input
                id="countryCode"
                value={formData.countryCode}
                onChange={(e) => setFormData({...formData, countryCode: e.target.value.toUpperCase()})}
                placeholder="TR, US, DE..."
                maxLength={10}
                data-testid="input-country-code"
              />
            </div>

            {/* Ülke Adı */}
            <div className="space-y-2">
              <Label htmlFor="countryDescription">Ülke Adı *</Label>
              <Input
                id="countryDescription"
                value={formData.countryDescription}
                onChange={(e) => setFormData({...formData, countryDescription: e.target.value})}
                placeholder="Türkiye, Amerika, Almanya..."
                data-testid="input-country-name"
              />
            </div>

            {/* Ülke Para Birimi */}
            <div className="space-y-2">
              <Label>Ülke Para Birimi</Label>
              <Select 
                value={formData.ulkeParaBirimi} 
                onValueChange={(v) => setFormData({...formData, ulkeParaBirimi: v})}
              >
                <SelectTrigger data-testid="select-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Navlun Bedeli */}
            <div className="space-y-2">
              <Label htmlFor="navlunBedeli">Navlun Bedeli</Label>
              <Input
                id="navlunBedeli"
                type="number"
                step="0.01"
                value={formData.navlunBedeli}
                onChange={(e) => setFormData({...formData, navlunBedeli: parseFloat(e.target.value) || 0})}
                data-testid="input-navlun"
              />
            </div>

            {/* Navlun Para Birimi */}
            <div className="space-y-2">
              <Label>Navlun Para Birimi</Label>
              <Select 
                value={formData.navlunParaBirimi} 
                onValueChange={(v) => setFormData({...formData, navlunParaBirimi: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Navlun İskontosu */}
            <div className="space-y-2">
              <Label htmlFor="navlunIskontosu">Navlun İskontosu</Label>
              <Input
                id="navlunIskontosu"
                type="number"
                step="0.01"
                value={formData.navlunIskontosu}
                onChange={(e) => setFormData({...formData, navlunIskontosu: parseFloat(e.target.value) || 0})}
                data-testid="input-iskonto"
              />
            </div>

            {/* Incoterm Code */}
            <div className="space-y-2">
              <Label>Incoterm Kodu</Label>
              <Select 
                value={formData.incotermCode} 
                onValueChange={(v) => setFormData({...formData, incotermCode: v})}
              >
                <SelectTrigger data-testid="select-incoterm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INCOTERM_CODES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Duruma Göre Incoterm */}
            <div className="space-y-2">
              <Label>Duruma Göre Incoterm</Label>
              <Select 
                value={formData.durumaGoreIncoterm} 
                onValueChange={(v) => setFormData({...formData, durumaGoreIncoterm: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INCOTERM_CODES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* İthalat Vergi */}
            <div className="space-y-2">
              <Label htmlFor="ithalatVergi">İthalat Vergisi</Label>
              <Input
                id="ithalatVergi"
                type="number"
                step="0.01"
                value={formData.ithalatVergi}
                onChange={(e) => setFormData({...formData, ithalatVergi: parseFloat(e.target.value) || 0})}
                data-testid="input-vergi"
              />
            </div>

            {/* Zone */}
            <div className="space-y-2">
              <Label>Zone</Label>
              <Select 
                value={formData.zone} 
                onValueChange={(v) => setFormData({...formData, zone: v})}
              >
                <SelectTrigger data-testid="select-zone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ZONES.map(z => (
                    <SelectItem key={z} value={z}>{z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-save"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editMode ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
            <DialogDescription>
              Bu Incoterm kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="btn-confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Result Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-500" />
              Excel Yükleme Sonucu
            </DialogTitle>
          </DialogHeader>
          
          {uploadResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{uploadResult.added || 0}</p>
                  <p className="text-sm text-green-700">Yeni Eklenen</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{uploadResult.updated || 0}</p>
                  <p className="text-sm text-blue-700">Güncellenen</p>
                </div>
              </div>
              
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">Hatalar:</p>
                  <ul className="text-xs text-red-600 space-y-1">
                    {uploadResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setUploadDialogOpen(false)}>
              Tamam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
